import { useState } from "react";
import { toast } from "sonner";

import { UPCOMING_CRUSADES } from "@/data/crusades";
import { autoCompleteFor, inputModeFor } from "@/lib/form-a11y";

import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const inputClass =
  "mt-2 w-full rounded-md border border-white/20 bg-white px-3 py-2 text-base text-foreground outline-none focus:ring-2 focus:ring-accent";

function Label({
  children,
  required,
  htmlFor,
}: {
  children: React.ReactNode;
  required?: boolean | undefined;
  htmlFor: string;
}) {
  return (
    <label htmlFor={htmlFor} className="block font-sans text-base font-semibold text-white">
      {children}
      {required ? <span className="ml-1 text-[#e8615b]">*</span> : null}
    </label>
  );
}

function Field({
  name,
  label,
  type = "text",
  required,
  className,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label htmlFor={name} required={required}>
        {label}
      </Label>
      <input id={name} name={name} type={type} required={required} className={inputClass} />
    </div>
  );
}

function TextArea({ name, label, required }: { name: string; label: string; required?: boolean }) {
  return (
    <div className="sm:col-span-2">
      <Label htmlFor={name} required={required}>
        {label}
      </Label>
      <textarea id={name} name={name} rows={5} required={required} className={inputClass} />
    </div>
  );
}

const COUNTRIES = [
  "United States",
  "Canada",
  "Mexico",
  "Bolivia",
  "Honduras",
  "Guatemala",
  "Zambia",
  "Kenya",
  "United Kingdom",
  "Other",
];

export function TripApplicationForm() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [citizen, setCitizen] = useState<"yes" | "no" | "">("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const value = (key: string) => {
      const raw = data.get(key);
      const text = typeof raw === "string" ? raw.trim() : "";
      return text.length > 0 ? text : null;
    };

    setSubmitting(true);
    const { error } = await supabase.from("trip_applications").insert({
      first_name: value("first_name") ?? "",
      trip: value("trip"),

      last_name: value("last_name") ?? "",
      address_line1: value("address_line1"),
      address_line2: value("address_line2"),
      city: value("city"),
      state_province: value("state_province"),
      postal_code: value("postal_code"),
      country: value("country"),
      phone: value("phone"),
      email: value("email") ?? "",
      date_of_birth: value("date_of_birth"),
      gender: value("gender"),
      us_citizen: citizen === "" ? null : citizen === "yes",
      nationality: value("nationality"),
      shirt_size: value("shirt_size"),
      church: value("church"),
      pastor: value("pastor"),
      emergency_contact: value("emergency_contact"),
      emergency_contact_number: value("emergency_contact_number"),
      family_members: value("family_members"),
      prior_missions_experience: value("prior_missions_experience"),
      additional_info: value("additional_info"),
    });
    setSubmitting(false);

    if (error) {
      toast.error("We couldn't send your application. Please try again.");
      setStatus("We couldn't send your application. Please try again.");
      return;
    }
    toast.success("Thank you! Your application has been received.");
    setStatus("Thank you! Your application has been received.");
    form.reset();
    setCitizen("");
  }

  return (
    <form onSubmit={handleSubmit} className="mt-10 grid gap-6 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Label htmlFor="trip" required>
          Which trip are you applying for?
        </Label>
        <select id="trip" name="trip" required defaultValue="" className={inputClass}>
          <option value="" />
          {UPCOMING_CRUSADES.map((crusade) => {
            const value = `${crusade.city}, ${crusade.country} — ${crusade.dates}`;
            return (
              <option key={value} value={value}>
                {value}
              </option>
            );
          })}
        </select>
      </div>

      <Field name="first_name" label="First Name" required />
      <Field name="last_name" label="Last Name" required />

      <div className="sm:col-span-2">
        <Label htmlFor="address_line1">Mailing Address</Label>
        <input
          id="address_line1"
          name="address_line1"
          className={inputClass}
          autoComplete={autoCompleteFor("address_line1")}
          inputMode={inputModeFor("address_line1")}
        />
        <input
          id="address_line2"
          name="address_line2"
          aria-label="Address line 2"
          className={inputClass}
          autoComplete={autoCompleteFor("address_line2")}
          inputMode={inputModeFor("address_line2")}
        />
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          <div>
            <input
              id="city"
              name="city"
              aria-label="City"
              className={cn(inputClass, "mt-0")}
              autoComplete={autoCompleteFor("city")}
              inputMode={inputModeFor("city")}
            />
            <span className="mt-1 block font-sans text-sm text-white/80">City</span>
          </div>
          <div>
            <input
              id="state_province"
              name="state_province"
              aria-label="State or province"
              className={cn(inputClass, "mt-0")}
              autoComplete={autoCompleteFor("state_province")}
              inputMode={inputModeFor("state_province")}
            />
            <span className="mt-1 block font-sans text-sm text-white/80">State/Province</span>
          </div>
          <div>
            <input
              id="postal_code"
              name="postal_code"
              aria-label="Zip or postal code"
              className={cn(inputClass, "mt-0")}
              autoComplete={autoCompleteFor("postal_code")}
              inputMode={inputModeFor("postal_code")}
            />
            <span className="mt-1 block font-sans text-sm text-white/80">Zip/Postal</span>
          </div>
        </div>
        <div className="mt-3">
          <select
            id="country"
            name="country"
            defaultValue="United States"
            aria-label="Country"
            className={cn(inputClass, "mt-0")}
          >
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          <span className="mt-1 block font-sans text-sm text-white/80">Country</span>
        </div>
      </div>

      <Field name="phone" label="Phone Number" type="tel" />
      <Field name="email" label="Email Address" type="email" required />
      <Field name="date_of_birth" label="Date of Birth" type="date" />

      <div>
        <Label htmlFor="gender" required>
          Gender
        </Label>
        <select id="gender" name="gender" required className={inputClass}>
          <option value="" />
          <option value="Female">Female</option>
          <option value="Male">Male</option>
        </select>
      </div>

      <fieldset>
        <legend className="font-sans text-base font-semibold text-white">
          U.S. Citizen<span className="ml-1 text-[#e8615b]">*</span>
        </legend>
        <div className="mt-2 space-y-2 font-sans text-white">
          <label className="flex items-center gap-3">
            <input
              type="radio"
              name="us_citizen"
              value="yes"
              required
              checked={citizen === "yes"}
              onChange={() => setCitizen("yes")}
              className="h-4 w-4"
            />
            Yes
          </label>
          <label className="flex items-center gap-3">
            <input
              type="radio"
              name="us_citizen"
              value="no"
              checked={citizen === "no"}
              onChange={() => setCitizen("no")}
              className="h-4 w-4"
            />
            No, my nationality is
          </label>
          {citizen === "no" ? (
            <input
              id="nationality"
              name="nationality"
              aria-label="Nationality"
              className={inputClass}
              autoComplete={autoCompleteFor("nationality")}
              inputMode={inputModeFor("nationality")}
            />
          ) : null}
        </div>
      </fieldset>

      <div>
        <Label htmlFor="shirt_size" required>
          Shirt Size
        </Label>
        <select id="shirt_size" name="shirt_size" required className={inputClass}>
          <option value="" />
          {["XS", "S", "M", "L", "XL", "2XL", "3XL"].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      <Field name="church" label="Church" required />
      <Field name="pastor" label="Pastor" />
      <Field name="emergency_contact" label="Emergency Contact" required />
      <Field name="emergency_contact_number" label="Emergency Contact Number" required />

      <TextArea name="family_members" label="Family members traveling with you" />
      <TextArea name="prior_missions_experience" label="Prior Missions Experience" required />
      <TextArea
        name="additional_info"
        label="Any additional information LOLI should be made aware of"
      />

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-accent px-8 py-4 font-sans font-semibold text-navy transition hover:brightness-110 disabled:opacity-60"
        >
          {submitting ? "Sending…" : "Submit"}
        </button>
      </div>
      <p role="status" aria-live="polite" className="sr-only">
        {status}
      </p>
    </form>
  );
}
